import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { convertISOStringToTimezone } from '@webapp/core/utils/dates/convertISOStringToTimezone'
import { type BudgetCategory } from '@webapp/platform/budgets'
import { stringifyAccountCode } from './stringifyAccountCode'
import { stringifySecondaryCategory } from './stringifySecondaryCategory'

interface BudgetBreakdownBar {
  label: string
  progressValue: number
  color: string
  striped?: boolean
}

const formatSpend = (rawValues: {
  approvedSpend: string
  currentRequestPendingSpend: string
  invoicedSpend: string
  pendingSpend: string
  purchasedSpend: string
  amount: Nullable<string>
  varianceAmount: Nullable<string>
  variancePercentage: Nullable<string>
}) => {
  const approvedSpend = parseFloat(rawValues.approvedSpend)
  const currentRequestPendingSpend = parseFloat(
    rawValues.currentRequestPendingSpend
  )
  const invoicedSpend = parseFloat(rawValues.invoicedSpend)
  const pendingSpend = parseFloat(rawValues.pendingSpend)
  const purchasedSpend = parseFloat(rawValues.purchasedSpend)
  const budgetAmount =
    rawValues.amount !== null ? parseFloat(rawValues.amount) : null
  const varianceAmount =
    rawValues.varianceAmount !== null
      ? parseFloat(rawValues.varianceAmount)
      : null
  const variancePercentage =
    rawValues.variancePercentage !== null
      ? parseFloat(rawValues.variancePercentage)
      : null

  const currentSpend = purchasedSpend + approvedSpend + invoicedSpend
  const spendAfterApproval = currentSpend + currentRequestPendingSpend
  const otherPendingRequests = Math.max(
    pendingSpend - currentRequestPendingSpend,
    0
  )
  const isOverBudget = varianceAmount !== null ? varianceAmount < 0 : false

  return {
    approvedSpend,
    invoicedSpend,
    pendingSpend,
    purchasedSpend,
    currentSpend,
    otherPendingRequests,
    spendAfterApproval,
    currentRequestPendingSpend,
    variance: varianceAmount,
    variancePercentage,
    budget: budgetAmount,
    isOverBudget,
  }
}

/**
 * @deprecated Use `formatBudgetCategories` instead. This function is for the pre-redesign data format.
 * To be removed when the budgets redesign is complete (FT_5817_ENABLE_BUDGETS_REDESIGN)
 */
export const formatBudgetCategoriesDeprecated = ({
  budgetCategories,
  headquartersTimezone,
  getBudgetBreakdown,
}: {
  budgetCategories: BudgetCategory[]
  headquartersTimezone: TimezonesEnum
  getBudgetBreakdown: (
    budgetedSpend: number,
    currentSpend: number,
    currentRequestSpend: number,
    spendAfterApproval: number,
    otherPendingRequests: number,
    approvedSpend?: number,
    purchasedSpend?: number,
    invoicedSpend?: number,
    showSpendBreakdown?: boolean
  ) => BudgetBreakdownBar[]
}) => {
  return budgetCategories.map((budget) => {
    const {
      id,
      uuid,
      name,
      startDate,
      endDate,
      spend: {
        approvedSpend,
        currentRequestPendingSpend,
        invoicedSpend,
        pendingSpend,
        purchasedSpend,
      },
      secondaryCategories,
      accountCodes,
      amount,
      interval,
      varianceAmount,
      variancePercentage,
    } = budget

    // Start and end dates are set according to the timezone of the headquarters which may differ from the user. As such, we
    // need to explicitly convert the dates to the headquarters timezone.
    const adjustedStartDate = convertISOStringToTimezone(
      startDate.toISOString(),
      headquartersTimezone
    )
    const adjustedEndDate = convertISOStringToTimezone(
      endDate.toISOString(),
      headquartersTimezone
    )

    const formattedSpendValues = formatSpend({
      approvedSpend,
      currentRequestPendingSpend,
      invoicedSpend,
      pendingSpend,
      purchasedSpend,
      amount,
      varianceAmount,
      variancePercentage,
    })

    const budgetBreakdown =
      formattedSpendValues.budget !== null
        ? getBudgetBreakdown(
            formattedSpendValues.budget,
            formattedSpendValues.currentSpend,
            formattedSpendValues.currentRequestPendingSpend,
            formattedSpendValues.spendAfterApproval,
            formattedSpendValues.otherPendingRequests,
            formattedSpendValues.approvedSpend,
            formattedSpendValues.purchasedSpend,
            formattedSpendValues.invoicedSpend,
            false
          )
        : []

    const secondaryCategoryStrings = secondaryCategories.map(
      stringifySecondaryCategory
    )
    const accountCodeStrings = accountCodes.map(stringifyAccountCode)

    return {
      id,
      uuid,
      name,
      startDate: adjustedStartDate,
      endDate: adjustedEndDate,
      budget:
        formattedSpendValues.budget !== null
          ? formattedSpendValues.budget.toString()
          : null,
      budgetBreakdown,
      currentSpend: formattedSpendValues.currentSpend.toString(),
      currentRequestSpend:
        formattedSpendValues.currentRequestPendingSpend.toString(),
      pendingSpend: formattedSpendValues.pendingSpend.toString(),
      period: interval,
      spendAfterApproval: formattedSpendValues.spendAfterApproval.toString(),
      otherPendingRequests:
        formattedSpendValues.otherPendingRequests.toString(),
      variance:
        formattedSpendValues.variance !== null
          ? formattedSpendValues.variance.toString()
          : null,
      variancePercentage:
        formattedSpendValues.variancePercentage !== null
          ? formattedSpendValues.variancePercentage.toString()
          : null,
      attributes: {
        departments: secondaryCategoryStrings,
        accountCodes: accountCodeStrings,
      },
      isOverBudget: formattedSpendValues.isOverBudget,
    }
  })
}
